<template>
  <div class="address-table">
    <BRow>
      <BCol cols="12">
          <div v-if="editing" class="mb-4">
              <font-awesome-icon icon="circle-info" class="text-info d-inline-block mr-2"/>
              <strong>Let op!</strong>
              Wijzigingen in de NAW gegevens zorgen niet automatisch voor een update naar achterliggende systemen of klant.
          </div>

        <BTableSimple small>
          <BTr>
            <BTh>Titel</BTh>
            <BTd>
              <template v-if="editing">
                <BFormInput v-model="localAddress.title" size="sm" />
              </template>
              <template v-else>
                {{ localAddress.title }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Bedrijfsnaam</BTh>
            <BTd>
              <template v-if="editing">
                <BFormInput v-model="localAddress.company_name" size="sm" />
              </template>
              <template v-else>
                {{ localAddress.company_name }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Naam</BTh>
            <BTd>
              <template v-if="editing">
                <span class="address-edit">
                    <span class="name">
                        <BFormInput v-model="localAddress.first_name"       size="sm" :placeholder="$t('omt.gui.location-management.address-first-name')" />
                        <BFormInput v-model="localAddress.last_name_prefix" size="sm" :placeholder="$t('omt.gui.location-management.address-last-name-prefix')" />
                        <BFormInput v-model="localAddress.last_name"        size="sm" :placeholder="$t('omt.gui.location-management.address-last-name')" />
                    </span>
                </span>
              </template>

              <template v-else>
                {{ formatDisplayNameForAddress(localAddress) }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Adres</BTh>
            <BTd>
              <template v-if="editing">
                <div class="address-edit">
                  <span class="street">
                    <BFormInput v-model="localAddress.street" size="sm" :placeholder="$t('omt.gui.location-management.address-street')" />
                  </span>
                  <span class="house">
                    <BFormInput v-model="localAddress.house_no"       size="sm" :placeholder="$t('omt.gui.location-management.address-houseno')" />
                    <BFormInput v-model="localAddress.house_no_extra" size="sm" :placeholder="$t('omt.gui.location-management.address-houseno-extra')" />
                </span>
            </div>
              </template>
              <template v-else>
                {{ localAddress.street }} {{ localAddress.house_no }} {{ localAddress.house_no_extra }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Postcode + Plaats</BTh>
            <BTd>
              <template v-if="editing">
                  <div class="address-edit">
                    <BFormInput v-model="localAddress.zipcode" size="sm" />
                    <BFormInput v-model="localAddress.city" size="sm" />
                  </div>
              </template>
              <template v-else>
                {{ localAddress.zipcode }} {{ localAddress.city }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Land</BTh>
            <BTd>
              <template v-if="editing">
                <BFormInput v-model="localAddress.country" size="sm" />
              </template>
              <template v-else>
                {{ localAddress.country }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Landcode</BTh>
            <BTd>
              <template v-if="editing">
                <BFormInput v-model="localAddress.iso_country_code" size="sm" />
              </template>
              <template v-else>
                {{ localAddress.iso_country_code }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>Telefoon</BTh>
            <BTd>
              <template v-if="editing">
                <BFormInput v-model="localAddress.phone_number" size="sm" />
              </template>
              <template v-else>
                {{ localAddress.phone_number }}
              </template>
            </BTd>
          </BTr>
          <BTr>
            <BTh>E-mail</BTh>
            <BTd>
                <template v-if="editing">
                    <BFormInput v-model="localAddress.email_address" size="sm" />
                </template>
                
                <template v-else>
                    <a v-if="isEmailAddressClickable" :href="emailHref">
                        {{ localAddress.email_address }}
                    </a>
                
                    <span v-else class="email-address">
                        {{ localAddress.email_address }}
                    </span>
              </template>
            </BTd>
          </BTr>
        </BTableSimple>
      </BCol>
    </BRow>

    <template v-if="editing">
      <BRow>
        <BCol cols="12">
            <OmniButtonCollection v-if="!saving">
                <OmniButton @click="cancelChanges"
                            variant="danger"
                            icon="times"
                >
                    Annuleren
                </OmniButton>

                <OmniButton @click="saveChanges"
                            :disabled="!canSaveChanges"
                            type="save"
                            variant="success"
                            icon="save"
                >
                    Opslaan
                </OmniButton>
            </OmniButtonCollection>

            <span v-else>
                <span class="icon">
                    <FontAwesomeIcon icon="spinner" spin />
                </span>
                <span class="label ml-1">
                    Wijzigingen opslaan&hellip;
                </span>
            </span>
        </BCol>
      </BRow>
    </template>
  </div>
</template>

<script>
import OmniButton           from '@/components/OmniStock/OmniButton.vue';
import OmniButtonCollection from '@/components/OmniStock/OmniButtonCollection.vue';

export default {
    name: "AddressTable",

    components: {
        OmniButton,
        OmniButtonCollection,
    },

    props: {
        address: {
            // This prop contains the address data.
            type:     Object,
            required: true,
            default:  {},
        },

        editable: {
            // This prop is used to determine if the component should be editable or not.
            type:     Boolean,
            required: false,
            default:  false,
        },

        editing: {
            // This prop is used to determine if the component should be in edit mode or not.
            type:     Boolean,
            required: false,
            default:  false,
        },

        saving: {
            // This prop is used by the parent component to disable the save button when saving the changes, to prevent double saving.
            type:     Boolean,
            required: false,
            default:  false,
        },

        orderNo: {
            type:     String,
            required: false,
            default:  '',
        }
    },

    data() {
        return {
            localAddress:  {}, // This is the local state of the address prop.
            // editing: false,  // This is the local state of the editing prop.
            displayPreferences: {
                buttons: {
                    iconsOnly: false,
                }
            },
        }
    },

    computed: {
        hasChanges() {
            // Checks if all key-value pairs in this.localAddress (local state) are equal to the ones in this.address (initial prop values), if not, return true.
            // Uses lodash isEqual to compare objects. https://lodash.com/docs/4.17.15#isEqual
            return !_.isEqual(this.localAddress, this.address);
        },

        canSaveChanges() {
            // Checks if there are changes, if the component is in edit mode and if the parent component is not already saving the changes.
            return this.hasChanges && this.editing && !this.saving;
        },

        orderCustomerEmailSubject() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'order-customer-email-subject') || '';
        },

        isEmailAddressClickable() {
            return (this.orderCustomerEmailSubject !== '');
        },

        emailHref() {
            if (this.orderNo === '' || this.orderCustomerEmailSubject === '') {
                return `mailto:${this.localAddress.email_address}`;
            }

            const parsedSubjectTemplate = this.parseStringTemplate(
                this.orderCustomerEmailSubject,
                {
                    'order_no': this.orderNo
                },
            );

            return encodeURI(`mailto:${this.localAddress.email_address}?subject=${parsedSubjectTemplate}`);
        }
    },

    mounted() {
        // Deep clone the address prop to the local state. https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
        this.localAddress = structuredClone(this.address);
    },

    methods: {
        saveChanges() {
            // Emit the new address to the parent component. Keep in mind that the parent component should update the prop value with the new address as well.
            this.$emit('save', this.localAddress);
        },
        cancelChanges() {
            // Revert the local state to the initial prop values, then set editing to false.
            this.localAddress = structuredClone(this.address);
            this.$emit('cancel');
        },

        /**
         * Formats the display name for an address object.
         * The function constructs the name using first_name, last_name_prefix, and last_name.
         * If these parts are all null or empty, it falls back to using full_name.
         * If full_name is also null or empty, it returns an empty string.
         *
         * @param {Object} address The address object containing name parts.
         * @param {string} [address.first_name] The first name.
         * @param {string} [address.last_name_prefix] The last name prefix.
         * @param {string} [address.last_name] The last name.
         * @param {string} [address.full_name] The full name.
         * @returns {string} The formatted display name.
         */
        formatDisplayNameForAddress(address) {
            if (!address) return '';

            const parts = [
                address.first_name,
                address.last_name_prefix,
                address.last_name
            ].filter(part => part).join(' ');

            return parts || address.full_name || '';
        },

        parseStringTemplate(stringTemplate, variables) {
            return Object.keys(variables).reduce((str, varName) => {
                return str.replace(`{${varName}}`, variables[varName]);
            }, stringTemplate);
        },
    },

    watch: {
        address: {
            handler: function (newVal) {
                // Watch prop 'address' for changes, then update the local state, so the parent becomes the single source of truth.
                this.localAddress = structuredClone(newVal);
            },
            deep: true,
        }
    }
}
</script>

<style scoped>
  .btn-container { display: flex; gap: .5rem; justify-content: flex-end; } /* Minimum space between the buttons. */
  .address-edit, .address-edit .house, .address-edit .name { display: flex; gap: .5rem; } /* Easy and consistent way of spacing form fields inside an element */
  .address-edit { flex-direction: column; }
</style>
